import {useCallback,useEffect,useRef,useState} from "react";
import {API} from "@/lib/api";

const rid=()=>globalThis.crypto?.randomUUID?.()||String(Date.now()+Math.random());
/** Each request owns its output. Cancelling a reply cannot undo dispatched tools. */
export function useChat(){
 const [messages,setMessages]=useState([]),[busy,setBusy]=useState(false);
 const conv=useRef(null),abortRef=useRef(null),current=useRef(null);
 const patch=(id,fn)=>setMessages(ms=>ms.map(m=>m.id===id?fn(m):m));
 useEffect(()=>()=>{current.current=null;abortRef.current?.abort()},[]);
 const send=useCallback(async(text,{image,onStart,onDone}={})=>{
  const responseId=rid();
  current.current=responseId;abortRef.current?.abort();
  const ctrl=new AbortController();abortRef.current=ctrl;
  const at=new Date();
  setMessages(ms=>[...ms,{id:rid(),role:"user",content:text,image,at},
   {id:responseId,role:"assistant",content:"",tools:[],at,pending:true}]);
  setBusy(true);onStart?.(responseId);
  let full="",error=null,idleTimer,totalTimer,timedOut=false;
  const timeout=()=>{timedOut=true;ctrl.abort()};
  const touch=()=>{clearTimeout(idleTimer);idleTimer=setTimeout(timeout,45000)};
  const clearTimers=()=>{clearTimeout(idleTimer);clearTimeout(totalTimer)};
  ctrl.signal.addEventListener("abort",clearTimers,{once:true});
  touch();totalTimer=setTimeout(timeout,180000);
  const active=()=>current.current===responseId&&!ctrl.signal.aborted;
  try{
   const r=await fetch(`${API}/chat/stream`,{
    method:"POST",credentials:"include",signal:ctrl.signal,headers:{"Content-Type":"application/json"},
    body:JSON.stringify({text,conversation_id:conv.current,image_base64:image?.split(",")[1],response_id:responseId})
   });
   if(!active())return;
   if(!r.ok)throw new Error(r.status===429?"Demasiadas solicitudes, espere un momento.":`Error ${r.status}`);
   const reader=r.body.getReader(),dec=new TextDecoder();let buf="";
   for(;;){
    const {value,done}=await reader.read();
    if(!active())break;
    if(done)break;
    touch();buf+=dec.decode(value,{stream:true});
    const chunks=buf.split("\n\n");buf=chunks.pop();
    for(const chunk of chunks){
     if(!active())break;
     if(!chunk.startsWith("data: "))continue;
     const ev=JSON.parse(chunk.slice(6));
     if(ev.response_id&&ev.response_id!==responseId)continue;
     if(ev.type==="start")conv.current=ev.conversation_id;
     else if(ev.type==="text"){full+=ev.content;patch(responseId,m=>({...m,content:m.content+ev.content}))}
     else if(ev.type==="tool"||ev.type==="tool_update")patch(responseId,m=>{
      const others=m.tools.filter(t=>t.command_id!==ev.command_id||!ev.command_id);
      return {...m,tools:ev.type==="tool_update"?[...others,ev]:[...m.tools,ev]};
     });
     else if(ev.type==="error"){error=ev;patch(responseId,m=>({...m,error:ev}))}
    }
   }
  }catch(e){
   if(current.current===responseId){
    if(timedOut){error={message:"La conexión dejó de responder. La respuesta parcial queda escrita; podés volver a intentarlo."};patch(responseId,m=>({...m,error}))}
    else if(e.name!=="AbortError"){error={message:e.message};patch(responseId,m=>({...m,error}))}
    else patch(responseId,m=>({...m,interrupted:true}));
   }
  }finally{
   clearTimers();ctrl.signal.removeEventListener("abort",clearTimers);
   patch(responseId,m=>({...m,pending:false}));
   if(current.current===responseId)setBusy(false);
  }
  if(current.current===responseId)onDone?.(full,responseId,error);
 },[]);
 const stop=useCallback(()=>{
  const id=current.current;current.current=null;abortRef.current?.abort();setBusy(false);
  if(id)setMessages(ms=>ms.map(m=>m.id===id?{...m,pending:false,interrupted:true}:m));
 },[]);
 const reset=useCallback(()=>{current.current=null;abortRef.current?.abort();conv.current=null;setMessages([]);setBusy(false)},[]);
 return {messages,busy,send,stop,reset};
}
